import React, { useEffect, useState } from "react";
import { api, isoDate, rangeFromKey } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import DateRangeFilter from "@/components/DateRangeFilter";

const CONFIGS = {
  education: { title: "Education Material", testid: "education-root",
    typeOptions: ["PPT","PDF","E-Book","Video","Audio","Infographic","Other"],
    statusOptions: ["Draft","Completed","Submitted","Published"],
    fields: ["title","type","status","remark"] },
  ppt: { title: "PPT / Education", testid: "ppt-root",
    purposeOptions: ["Institute Students","Internal Training","Patient Education","Doctor Training","Other"],
    fields: ["topic","count","purpose","file_link","remark"] },
  shoots: { title: "Educational Shoots", testid: "shoots-root",
    fields: ["completed","count","topic","shoot_type","remark"] },
  training: { title: "Training", testid: "training-root",
    trainingTypes: ["Doctor Training","Clinical Training","Student Session","Institute Session","Other"],
    fields: ["completed","topic","training_type","remark"] },
  rd: { title: "R&D", testid: "rd-root",
    categoryOptions: ["Existing Treatment Improvement","Existing Procedure Improvement","Existing Surgery Improvement","Treatment Protocol","Surgery Protocol","Clinical Guideline","Non-Surgical Procedure","New Surgery","New Treatment","New Procedure","New Protocol","New Guideline","New Non-Surgical Procedure","New Technology","Other"],
    statusOptions: ["Pending","Under Review","In Progress","Approved","Rejected"],
    fields: ["title","category","description","objective","expected_benefit","status","next_step"] },
};

export default function ActivityPage({ activityType = "education" }) {
  const cfg = CONFIGS[activityType];
  const { user, doctor } = useAuth();
  const isAdmin = ["founder","manager"].includes(user?.role);
  const [doctors, setDoctors] = useState([]);
  const [docId, setDocId] = useState(null);
  const [range, setRange] = useState({ key: "this_month", ...rangeFromKey("this_month") });
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ date: isoDate(new Date()) });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/doctors${isAdmin ? "?status=active" : ""}`).then(r => {
      setDoctors(r.data);
      if (user?.role === "doctor" && doctor) setDocId(doctor.doctor_id);
      else if (r.data[0]) setDocId(r.data[0].doctor_id);
    });
  }, [user, doctor, isAdmin]);

  useEffect(() => {
    if (!range.from || !range.to) return;
    const scope = user?.role === "doctor" ? `&doctor_id=${doctor?.doctor_id}` : (docId ? `&doctor_id=${docId}` : "");
    api.get(`/activities?type=${activityType}&date_from=${range.from}&date_to=${range.to}${scope}`).then(r => setItems(r.data));
  }, [activityType, range, docId, doctor, user]);

  const save = async () => {
    if (!docId) return;
    setSaving(true);
    try {
      await api.post("/activities", { type: activityType, doctor_id: docId, date: form.date, data: form });
      toast.success(`${cfg.title} saved`);
      setForm({ date: isoDate(new Date()) });
      const scope = user?.role === "doctor" ? `&doctor_id=${doctor?.doctor_id}` : (docId ? `&doctor_id=${docId}` : "");
      const r = await api.get(`/activities?type=${activityType}&date_from=${range.from}&date_to=${range.to}${scope}`);
      setItems(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`/activities/${id}`);
    setItems(items.filter(x => x.activity_id !== id));
  };

  const renderField = (f) => {
    const dropdown = (key, opts, placeholder) => (
      <Select value={form[key] || ""} onValueChange={v => setForm(x => ({ ...x, [key]: v }))}>
        <SelectTrigger className="bg-navy-800 border-navy-700" data-testid={`af-${key}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>{opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
    if (f === "type") return dropdown("type", cfg.typeOptions, "Type");
    if (f === "status") return dropdown("status", cfg.statusOptions, "Status");
    if (f === "purpose") return dropdown("purpose", cfg.purposeOptions, "Purpose");
    if (f === "category") return dropdown("category", cfg.categoryOptions, "Category");
    if (f === "training_type") return dropdown("training_type", cfg.trainingTypes, "Training Type");
    if (f === "completed") return (
      <div className="flex gap-2">
        {["Y","N"].map(v => (
          <Button key={v} size="sm" variant={form.completed === v ? "default" : "outline"} onClick={() => setForm(x => ({ ...x, completed: v }))}
            className={form.completed === v ? "gold-bg hover:opacity-90" : "border-navy-700 text-muted-foreground"}>
            {v === "Y" ? "Yes" : "No"}
          </Button>
        ))}
      </div>
    );
    if (f === "count") return <Input type="number" placeholder="Count" value={form.count || ""} onChange={e => setForm(x => ({ ...x, count: e.target.value }))} className="bg-navy-800 border-navy-700 num" data-testid="af-count" />;
    if (["description","objective","expected_benefit","next_step","remark","remarks"].includes(f))
      return <Textarea rows={2} placeholder={f.replaceAll("_"," ")} value={form[f] || ""} onChange={e => setForm(x => ({ ...x, [f]: e.target.value }))} className="bg-navy-800 border-navy-700 resize-none" data-testid={`af-${f}`} />;
    return <Input placeholder={f.replaceAll("_"," ")} value={form[f] || ""} onChange={e => setForm(x => ({ ...x, [f]: e.target.value }))} className="bg-navy-800 border-navy-700" data-testid={`af-${f}`} />;
  };

  return (
    <div className="space-y-6" data-testid={cfg.testid}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Doctor Activity</div>
          <h1 className="mt-2 text-3xl font-heading font-medium">{cfg.title}</h1>
          <div className="gold-underline mt-3" />
        </div>
        {isAdmin && (
          <Select value={docId || ""} onValueChange={setDocId}>
            <SelectTrigger className="w-56 bg-navy-800 border-navy-700" data-testid="act-doctor-select"><SelectValue placeholder="Doctor" /></SelectTrigger>
            <SelectContent>{doctors.map(d => <SelectItem key={d.doctor_id} value={d.doctor_id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      <DateRangeFilter value={range} onChange={setRange} />

      <Card className="bg-navy-800 border-navy-700">
        <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Add New Entry</CardTitle></CardHeader>
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Date</Label>
            <Input type="date" value={form.date} onChange={e => setForm(x => ({ ...x, date: e.target.value }))} className="mt-1.5 bg-navy-800 border-navy-700" data-testid="af-date" />
          </div>
          {cfg.fields.map(f => (
            <div key={f} className={["description","objective","expected_benefit","next_step","remark"].includes(f) ? "md:col-span-3" : ""}>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{f.replaceAll("_"," ")}</Label>
              <div className="mt-1.5">{renderField(f)}</div>
            </div>
          ))}
          <div className="md:col-span-3">
            <Button onClick={save} disabled={saving || !docId} className="gold-bg hover:opacity-90" data-testid="af-save-btn"><Plus className="h-4 w-4 mr-2" />{saving ? "Saving…" : "Save Entry"}</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-navy-800 border-navy-700">
        <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Records ({items.length})</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-900/60 uppercase text-[10px] tracking-widest text-muted-foreground">
              <tr><th className="text-left px-5 py-3">Date</th><th className="text-left px-5 py-3">Doctor</th><th className="text-left px-5 py-3">Details</th><th className="text-left px-5 py-3">Status</th><th className="text-right px-5 py-3">Actions</th></tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={5} className="px-5 py-6 text-center text-muted-foreground">No records in this period.</td></tr>}
              {items.map(it => (
                <tr key={it.activity_id} className="border-t border-navy-700" data-testid={`row-${it.activity_id}`}>
                  <td className="px-5 py-3">{it.date}</td>
                  <td className="px-5 py-3 text-muted-foreground">{doctors.find(d => d.doctor_id === it.doctor_id)?.name || "—"}</td>
                  <td className="px-5 py-3">
                    <div className="space-y-1">
                      {Object.entries(it.data || {}).filter(([k]) => !["date"].includes(k)).slice(0, 3).map(([k,v]) => (
                        <div key={k} className="text-xs"><span className="text-muted-foreground">{k.replaceAll("_"," ")}:</span> <span>{String(v)}</span></div>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {it.data?.status && <Badge className="bg-gold/10 gold-text border border-gold/30 text-[10px]">{it.data.status}</Badge>}
                    {it.data?.completed && <Badge className={`text-[10px] border-0 ${it.data.completed === "Y" ? "rag-green" : "rag-amber"}`}>{it.data.completed === "Y" ? "Completed" : "Pending"}</Badge>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {(isAdmin || it.doctor_id === doctor?.doctor_id) && (
                      <button onClick={() => remove(it.activity_id)} className="text-muted-foreground hover:text-rose_alert" data-testid={`del-${it.activity_id}`}><Trash2 className="h-4 w-4 inline" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
